import { Button, Card } from "@mui/material";
import React, { useEffect, useState } from "react";
import { CustomFooterItem } from "../types/footer";
import { Pagination } from "../types/form";
import { getImageUrl } from "../utils";
import { getAllCustomFooters, getAllGLobalFooters } from "../utils/requests";

interface FooterThemeSelectProps {
	onSelectFooter: (footer: CustomFooterItem) => void;
}

const AllFooterSelectModal: React.FC<FooterThemeSelectProps> = ({
	onSelectFooter,
}) => {
	const [footers, setFooters] = useState<CustomFooterItem[]>([]);
	const [pagination, setPagination] = useState<Pagination | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [selectedFooter, setSelectedFooter] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const [type, setType] = useState<"local" | "global">("local");

	useEffect(() => {
		fetchFooters(1, 10, type);
	}, [type]);

	const fetchFooters = async (
		page = 1,
		perPage = 10,
		type: "local" | "global" = "local"
	) => {
		try {
			setLoading(true);
			const response = await (type === "global"
				? getAllGLobalFooters
				: getAllCustomFooters)({
				perPage,
				page,
			});

			setFooters(response.data);
			setPagination(response.page);
			setError(null);
		} catch (err) {
			setError("Error fetching headers. Please try again.");
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	const handleSelectFooter = (footer: CustomFooterItem) => {
		setSelectedFooter(footer._id);
		onSelectFooter(footer);
	};

	const renderFooterPreview = (footer: CustomFooterItem) => {
		if (footer.images) {
			return (
				<div className="h-32 w-full overflow-hidden rounded-t-md">
					<img
						src={footer.images}
						alt={footer.name}
						className="w-full h-full object-cover"
					/>
				</div>
			);
		} else if (footer.settings?.content) {
			return (
				<div
					className="h-80 w-full overflow-hidden rounded-t-md bg-gray-100"
					style={{
						zoom: 0.3,
					}}
					dangerouslySetInnerHTML={{
						__html: footer.settings.content,
					}}
				/>
			);
		} else {
			return (
				<div className="h-32 w-full bg-gray-200 rounded-t-md flex items-center justify-center text-gray-500">
					No preview available
				</div>
			);
		}
	};

	if (loading && footers.length === 0) {
		return <div className="flex justify-center p-8">Loading footer...</div>;
	}

	if (error) {
		return <div className="text-red-500 p-4">{error}</div>;
	}

	return (
		<div className="container mx-auto p-4 min-w-[600px] max-h-[70vh] overflow-y-auto">
			<h2 className="text-xl font-bold mb-6">Select Footer Theme</h2>

			<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
				{/* select type */}
				<div className="col-span-3 flex justify-end gap-4 mb-4">
					<Button
						variant={type === "local" ? "contained" : "outlined"}
						className={`mr-2 ${
							type === "local" ? "bg-blue-500 text-white" : ""
						}`}
						onClick={() => setType("local")}>
						Local Footers
					</Button>
					<Button
						variant={type === "global" ? "contained" : "outlined"}
						className={`${
							type === "global" ? "bg-blue-500 text-white" : ""
						}`}
						onClick={() => setType("global")}>
						Global Footers
					</Button>
				</div>

				{footers.map((footer) => (
					<Card
						key={footer._id}
						className={`cursor-pointer transition-all duration-200 hover:shadow-md overflow-hidden ${
							selectedFooter === footer._id
								? "ring-2 ring-blue-500"
								: ""
						}`}
						onClick={() => handleSelectFooter(footer)}>
						{footer.images ? (
							<img
								src={getImageUrl(footer.images)}
								alt={footer.name}
								className="w-full h-72 object-cover"
							/>
						) : (
							renderFooterPreview(footer)
						)}

						<div className="p-4">
							<div className="flex justify-between items-start">
								<div>
									<h3 className="font-semibold truncate">
										{footer.name}
									</h3>
									<p className="text-sm text-gray-600 mt-1 line-clamp-2">
										{footer.description || "No description"}
									</p>
									<div className="flex mt-2 space-x-2">
										<span className="text-xs bg-gray-100 px-2 py-1 rounded">
											{footer.screenMode}
										</span>
										{footer.isFrontHeader && (
											<span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
												Front
											</span>
										)}
									</div>
								</div>
							</div>
						</div>
					</Card>
				))}
			</div>

			{footers.length === 0 && !loading && (
				<div className="text-center py-8">
					<p className="text-gray-500">No footer themes found</p>
				</div>
			)}

			{pagination && pagination.totalPage > 1 && (
				<div className="flex justify-center mt-6 space-x-2">
					<Button
						variant="outlined"
						disabled={pagination.currentPage <= 1}
						onClick={() =>
							fetchFooters(pagination.currentPage - 1, 10, type)
						}>
						Previous
					</Button>

					<span className="py-2 px-4">
						Page {pagination.currentPage} of {pagination.totalPage}
					</span>

					<Button
						variant="outlined"
						disabled={
							pagination.currentPage >= pagination.totalPage
						}
						onClick={() =>
							fetchFooters(pagination.currentPage + 1, 10, type)
						}>
						Next
					</Button>
				</div>
			)}
		</div>
	);
};

export default AllFooterSelectModal;
